export enum Event {
    stateChange = 'state-change',
    error = 'error',
    customAction = 'custom-action',
    clientAction = 'client-action',
    dispose = 'dispose',
    reset = 'reset',
    metadata = 'metadata',
    notification = 'notification',
    analytics = 'analytics',
    resize = 'resize',

    alwaysShowControlBar = 'always-show-control-bar',
    play = 'play',
    pause = 'pause',
    enterFullScreen = 'enter-full-screen',
    exitFullScreen = 'exit-full-screen',
    enterNativeFullScreen = 'enter-native-full-screen',
    exitNativeFullScreen = 'exit-native-full-screen',
    seek = 'seek',
    mute = 'mute',
    unmute = 'unmute',
    changeVolume = 'change-volume',
    selectSubtitleTrack = 'select-subtitle-track',
    disableSubtitles = 'disable-subtitles',
    disableNotifications = 'disable-notifications',

    selectLevel = 'select-level',
}

const blockingEvents = new Set([
    Event.play,
    Event.pause,
    Event.mute,
    Event.unmute,
    Event.stateChange,
]);

export type Unsubscriber = () => void

export type Handler = (payload: unknown, context?: string) => void | Promise<void>;

export default class EventBus {
    private handlers: Map<string, Handler[]>;

    constructor() {
        this.handlers = new Map();
    }

    public on(type: Event, handler: Handler): Unsubscriber {
        const handlers = [
            ...(this.handlers.get(type) ?? []),
            handler
        ];

        this.handlers.set(type, handlers);

        return () => {
            this.off(type, handler);
        };
    }

    public off(type: Event, handler: Handler): void {
        const handlers = this.handlers.get(type) ?? [];

        this.handlers.set(type, handlers.filter(h => h !== handler));
    }

    public emit(type: Event, payload?: unknown, context?: string): void {
        const handlers = this.handlers.get(type) ?? [];

        for (const handler of handlers) {
            // Some events should be handled sequentially
            // For example the "unmute -> browser force pause -> tryAutoPlay -> mute back -> resume" sequence
            // might result in an incorrect muted state in the controls if not processed in order
            if (blockingEvents.has(type)) {
                try {
                    handler(payload, context);
                } catch (e) {
                    console.error('[EventBus] handler error', e);
                }
            } else {
                setTimeout(() => {
                    try {
                        handler(payload, context);
                    } catch (e) {
                        console.error('[EventBus] handler error', e);
                    }
                });
            }
        }
    }
}
